import { useState, type ReactNode } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { ArrowLeft, ArrowRight, Briefcase, Building2, ChevronRight, CalendarClock, CheckSquare, ChevronDown, FolderKanban, GitBranch, Mail, MessageCircle, Pencil, Phone, Plus, StickyNote, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { crm2Api, useCrm2Account, useCrm2Accounts, useCrm2Write, type AccountDetail, type Deal } from './api'
import { DealSheet } from './DealSheet'
import { EditContact } from './EditContact'
import { NewDealDialog } from './NewDealDialog'
import { Timeline, type LogKind } from './Timeline'
import { dateInput, Dot, Field, fieldClass, fromDateInput, money, PersonSelect, PHONE_PLACEHOLDER, shortDay, LINK_CHIP, usePeopleNames, whatsappLink } from './shared'

const PIPE: Record<string, string> = { marketing: 'Marketing', sales: 'Sales', post_sales: 'Post-sales', partnership: 'Partnership', podcast: 'Podcast' }
const TALK = new Set(['email', 'call', 'meeting', 'whatsapp', 'note'])

function ago(iso: string | null | undefined, now: number) {
  if (!iso) return '—'
  const n = Math.floor((now - Date.parse(iso)) / 86_400_000)
  return n <= 0 ? 'today' : n === 1 ? 'yesterday' : `${n} days ago`
}

/**
 * One company, laid out like the CRM record people already know: who it is and what to do on the
 * left, what happened in the middle, its people and deals on the right. A main account also shows
 * its sub-accounts (regions, departments) and every deal under them.
 */
export function AccountPage() {
  const { accountId = '' } = useParams()
  const [params, setParams] = useSearchParams()
  const q = useCrm2Account(accountId)
  const openId = params.get('deal')
  const open = (id: string | null) => {
    const next = new URLSearchParams(params)
    if (id) next.set('deal', id)
    else next.delete('deal')
    setParams(next, { replace: true })
  }
  return (
    <div className="mx-auto w-full max-w-[1600px]">
      {q.error ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : q.isPending ? <LoadingState label="Loading the account" /> : <Body key={`${q.data.account.id}:${q.data.account.version}`} d={q.data} onOpen={open} />}
      <DealSheet id={openId} onClose={() => open(null)} onOpen={open} />
    </div>
  )
}

function Card({ title, count, children, action, defaultOpen = true }: { title: string; count?: number; children: ReactNode; action?: ReactNode; defaultOpen?: boolean }) {
  const [on, setOn] = useState(defaultOpen)
  return (
    <section className="rounded-xl border border-border bg-card shadow-sm">
      <div className="flex items-center gap-2 px-4 py-3">
        <button type="button" onClick={() => setOn(!on)} className="inline-flex items-center gap-1.5 text-sm font-semibold" aria-expanded={on}>
          <ChevronDown className={cn('size-4 transition-transform', !on && '-rotate-90')} />
          {title}
        </button>
        {count !== undefined ? <span className="text-xs text-muted-foreground">{count}</span> : null}
        <span className="ml-auto">{action}</span>
      </div>
      {on ? <div className="border-t border-border-soft">{children}</div> : null}
    </section>
  )
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="border-b border-border-soft px-4 py-2.5 last:border-b-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm font-medium">{children}</div>
    </div>
  )
}

function Body({ d, onOpen }: { d: AccountDetail; onOpen: (id: string) => void }) {
  const { workspaceSlug = '' } = useParams()
  const name = usePeopleNames()
  const a = d.account
  const [tab, setTab] = useState<'overview' | 'activities' | 'communications'>('overview')
  const [kind, setKind] = useState<LogKind>('note')
  const [newDeal, setNewDeal] = useState(false)
  const [now] = useState(() => Date.now())
  const talk = d.timeline.filter((x) => TALK.has(x.kind))
  // The account's own deals, then each sub-account's, so a main account lists the whole enterprise in one place.
  const allDeals: Array<Deal & { via: string | null }> = [...d.deals.map((x) => ({ ...x, via: null })), ...d.treeDeals]
  const quick = (k: LogKind) => {
    setKind(k)
    setTab('activities')
  }
  const lifecycle = d.highlights.lifecycle

  return (
    <div className="grid gap-4 xl:grid-cols-[20rem_minmax(0,1fr)_21rem]">
      {/* Left: who it is and what to do */}
      <div className="grid content-start gap-4">
        <section className="grid gap-3 rounded-xl border border-border bg-card p-4 shadow-sm">
          <Link to={paths.crm2Accounts(workspaceSlug)} className={LINK_CHIP}>
            <ArrowLeft className="size-4" />
            Accounts
          </Link>
          {d.parent ? (
            <Link to={paths.crm2Account(workspaceSlug, d.parent.id)} className={LINK_CHIP}>
              <GitBranch className="size-3.5" />
              Part of {d.parent.name}
            </Link>
          ) : null}
          <div className="flex items-start gap-3">
            <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-muted text-lg font-semibold">{a.name.slice(0, 1).toUpperCase()}</span>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold">{a.name}</h1>
              {a.domain ? <p className="truncate font-mono text-xs text-muted-foreground">{a.domain}</p> : null}
              {d.highlights.owners.length ? (
                <p className="mt-1 inline-flex max-w-full items-center gap-1 truncate rounded-md bg-muted px-2 py-0.5 text-xs">
                  <User className="size-3.5 shrink-0" />
                  {d.highlights.owners.map((o) => o.name).join(', ')}
                </p>
              ) : null}
            </div>
          </div>
          {lifecycle ? <span className="w-fit rounded-full bg-brand px-2.5 py-0.5 text-xs font-semibold text-white">● {lifecycle}</span> : null}
          <div className="grid grid-cols-6 gap-1 pt-1">
            {(
              [
                ['note', 'Note', StickyNote],
                ['email', 'Email', Mail],
                ['call', 'Call', Phone],
                ['task', 'Task', CheckSquare],
                ['meeting', 'Meeting', CalendarClock],
                ['whatsapp', 'WhatsApp', MessageCircle],
              ] as const
            ).map(([k, label, Icon]) => (
              <button key={k} type="button" onClick={() => quick(k)} className="grid justify-items-center gap-1 text-tiny text-muted-foreground hover:text-foreground">
                <span className="grid size-9 place-items-center rounded-full border border-border bg-background">
                  <Icon className="size-4" />
                </span>
                {label}
              </button>
            ))}
          </div>
        </section>

        <Card title="Account highlights">
          <Fact label="Lifecycle (furthest stage)">{lifecycle ?? '—'}</Fact>
          <Fact label="Account owner">{d.highlights.owners.map((o) => o.name).join(', ') || '—'}</Fact>
          <Fact label="Lead source">{d.highlights.source ?? '—'}</Fact>
          <Fact label="First seen">{shortDay(d.highlights.firstSeen)}</Fact>
          <Fact label="Last activity">{ago(d.highlights.lastActivityAt, now)}</Fact>
          <Fact label="Renews">{a.renewalAt ? shortDay(a.renewalAt) : '—'}</Fact>
        </Card>

        <Properties d={d} />
      </div>

      {/* Middle: what happened */}
      <div className="grid min-w-0 content-start gap-4">
        <section className="rounded-xl border border-border bg-card shadow-sm">
          <div className="flex items-center gap-1 border-b border-border-soft px-3">
            {(
              [
                ['overview', 'Overview', null],
                ['activities', 'Activities', d.timeline.length],
                ['communications', 'Communications', talk.length],
              ] as const
            ).map(([k, label, n]) => (
              <button key={k} type="button" onClick={() => setTab(k)} className={cn('-mb-px border-b-2 px-3 py-3 text-sm', tab === k ? 'border-brand font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground')}>
                {label}
                {n !== null ? <span className="ml-1.5 rounded-full bg-muted px-1.5 text-tiny">{n}</span> : null}
              </button>
            ))}
          </div>

          {tab === 'overview' ? (
            <div className="grid gap-5 p-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <Tile label="First seen">{shortDay(d.highlights.firstSeen)}</Tile>
                <Tile label="Lifecycle stage">{lifecycle ?? '—'}</Tile>
                <Tile label="Last activity">{ago(d.highlights.lastActivityAt, now)}</Tile>
              </div>
              <div className="grid gap-2">
                <div className="flex items-center">
                  <h2 className="text-sm font-semibold">Recent activities</h2>
                  <button type="button" onClick={() => setTab('activities')} className={cn(LINK_CHIP, 'ml-auto')}>
                    View all
                  </button>
                </div>
                <ol className="divide-y divide-border-soft">
                  {d.timeline.slice(0, 6).map((x) => (
                    <li key={x.id} className="flex gap-3 py-2.5 text-sm">
                      <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-muted text-tiny font-semibold uppercase">{x.kind.slice(0, 2)}</span>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">{x.subject ?? x.kind}</div>
                        {x.body ? <p className="line-clamp-2 text-muted-foreground">{x.body}</p> : null}
                      </div>
                      <span className="shrink-0 text-xs text-muted-foreground">{shortDay(x.occurredAt)}</span>
                    </li>
                  ))}
                  {!d.timeline.length ? <li className="py-2 text-sm text-muted-foreground">Nothing yet.</li> : null}
                </ol>
              </div>
              <SubAccounts d={d} onOpen={onOpen} />
            </div>
          ) : tab === 'activities' ? (
            <div className="p-3">
              <Timeline accountId={a.id} items={d.timeline} contacts={d.contacts} kind={kind} onKind={setKind} title="All activity" />
            </div>
          ) : (
            <div className="p-3">
              <Timeline accountId={a.id} items={talk} contacts={d.contacts} kind={kind === 'task' ? 'note' : kind} onKind={setKind} title="Communications" />
            </div>
          )}
        </section>
      </div>

      {/* Right: people and deals */}
      <div className="grid content-start gap-4">
        <ContactsCard d={d} />
        <Card
          title="Deals"
          count={allDeals.length}
          action={
            <Button size="sm" onClick={() => setNewDeal(true)}>
              <Plus className="size-4" />
              Add deal
            </Button>
          }
        >
          <ul className="divide-y divide-border-soft">
            {allDeals.map((x) => (
              <li key={x.id}>
                <button type="button" onClick={() => onOpen(x.id)} className="grid w-full gap-0.5 px-4 py-2.5 text-left text-sm hover:bg-accent">
                  <span className={cn(LINK_CHIP, 'text-sm')}>{x.name}</span>
                  {x.via ? <span className="text-xs text-muted-foreground">via {x.via}</span> : null}
                  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    {x.status === 'open' ? <Dot level={x.fresh.level} /> : null}
                    {PIPE[x.pipeline]} · {x.status === 'open' ? `${x.groupLabel}${x.statusName ? ` · ${x.statusName}` : ''}` : x.status === 'won' ? 'Won' : 'Lost'} · {x.fresh.daysInStage}d in stage
                    {x.value ? ` · ${money(x.value)}` : ''}
                  </span>
                  <span className="text-xs text-muted-foreground">{name(x.ownerId)}</span>
                </button>
              </li>
            ))}
            {!allDeals.length ? <li className="px-4 py-3 text-sm text-muted-foreground">No deals yet.</li> : null}
          </ul>
        </Card>
        <Card title="TapPM" count={d.projects.length}>
          <div className="grid gap-2 p-3">
            {d.projects.map((p) => (
              <Button key={p.id} asChild size="sm" variant="outline" className="justify-start">
                <Link to={paths.pm2Project(workspaceSlug, p.id)}>
                  <FolderKanban className="size-4" />
                  Open TapPM · {p.phaseLabel}
                  {p.cycle > 1 ? ` (cycle ${p.cycle})` : ''}
                  <ArrowRight className="ml-auto size-4" />
                </Link>
              </Button>
            ))}
            {!d.projects.length ? <p className="text-sm text-muted-foreground">Opens when a deal reaches Pre-Pilot.</p> : null}
          </div>
        </Card>
      </div>
      {newDeal ? <NewDealDialog pipeline="marketing" account={{ id: a.id, name: a.name }} onClose={() => setNewDeal(false)} onCreated={onOpen} /> : null}
    </div>
  )
}

function Tile({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-lg bg-muted/60 px-4 py-3">
      <div className="text-tiny font-semibold uppercase text-muted-foreground">{label}</div>
      <div className="mt-1 text-base font-semibold">{children}</div>
    </div>
  )
}

/** Regions or departments under this account, each its own account with its own deals and people. */
function SubAccounts({ d, onOpen }: { d: AccountDetail; onOpen: (id: string) => void }) {
  const all = useCrm2Accounts('')
  const [mode, setMode] = useState<'none' | 'new' | 'existing'>('none')
  const [newName, setNewName] = useState('')
  const [pick, setPick] = useState('')
  const create = useCrm2Write(() => crm2Api.createAccount({ name: newName.trim(), parentId: d.account.id }), (r) => `${r.name} added under ${d.account.name}`)
  const place = useCrm2Write(({ id, parentId }: { id: string; parentId: string | null }) => crm2Api.setParent(id, parentId), (r) => (r.parentId ? 'Moved under this account' : 'Back at the top'))
  const candidates = (all.data ?? []).filter((x) => x.id !== d.account.id && x.parentId !== d.account.id && x.id !== d.parent?.id)
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="whitespace-nowrap text-sm font-semibold">
          Sub-accounts <span className="font-normal text-muted-foreground">{d.children.length}</span>
        </h2>
        <div className="ml-auto flex gap-3 whitespace-nowrap text-sm">
          <Button size="sm" variant="outline" onClick={() => setMode(mode === 'new' ? 'none' : 'new')} aria-pressed={mode === 'new'}>
            <Plus className="size-4" />
            New sub-account
          </Button>
          <Button size="sm" variant="outline" onClick={() => setMode(mode === 'existing' ? 'none' : 'existing')} aria-pressed={mode === 'existing'}>
            <GitBranch className="size-4" />
            Link existing
          </Button>
        </div>
        <p className="basis-full text-xs text-muted-foreground">Regions or departments, each with its own deals and people.</p>
      </div>
      {mode === 'new' ? (
        <div className="flex flex-wrap gap-2 rounded-lg border border-border-soft p-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={`${d.account.name} India`} className="h-8 min-w-56 flex-1" aria-label="Sub-account name" />
          <Button size="sm" disabled={!newName.trim() || create.isPending} onClick={() => create.mutate(undefined, { onSuccess: () => { setNewName(''); setMode('none') } })}>
            Add
          </Button>
        </div>
      ) : null}
      {mode === 'existing' ? (
        <div className="flex flex-wrap gap-2 rounded-lg border border-border-soft p-2">
          <select value={pick} onChange={(e) => setPick(e.target.value)} className={cn(fieldClass, 'h-8 min-w-56 flex-1')} aria-label="Account to place under this one">
            <option value="">Pick an account</option>
            {candidates.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
          <Button size="sm" disabled={!pick || place.isPending} onClick={() => place.mutate({ id: pick, parentId: d.account.id }, { onSuccess: () => { setPick(''); setMode('none') } })}>
            Place under {d.account.name}
          </Button>
        </div>
      ) : null}
      {d.children.length ? (
        <Tree d={d} onOpen={onOpen} onMoveTop={(id) => place.mutate({ id, parentId: null })} />
      ) : mode === 'none' ? (
        <p className="text-sm text-muted-foreground">None. For a big enterprise, add one per region or department and put their deals there.</p>
      ) : null}
    </div>
  )
}

function ContactsCard({ d }: { d: AccountDetail }) {
  const { workspaceSlug = '' } = useParams()
  const add = useCrm2Write((c: { name: string; title: string | null; email: string | null; phone: string | null }) => crm2Api.addAccountContact(d.account.id, c), () => 'Contact added')
  const [adding, setAdding] = useState(false)
  const [c, setC] = useState({ name: '', title: '', email: '', phone: '' })
  const [editId, setEditId] = useState<string | null>(null)
  return (
    <Card title="Contacts" count={d.contacts.length}>
      <ul className="max-h-[28rem] divide-y divide-border-soft overflow-y-auto">
        {d.contacts.map((p) =>
          editId === p.id ? (
            <li key={p.id} className="px-4 py-2.5 text-sm">
              <EditContact c={p} onDone={() => setEditId(null)} />
            </li>
          ) : (
          <li key={p.id} className="grid gap-0.5 px-4 py-2.5 text-sm">
            <div className="flex items-center gap-2">
              <Link to={paths.crm2Contact(workspaceSlug, p.id)} className={cn(LINK_CHIP, 'text-sm')}>
                {p.name}
              </Link>
              <button type="button" onClick={() => setEditId(p.id)} className={cn(LINK_CHIP, 'ml-auto text-muted-foreground')} aria-label={`Edit ${p.name}`}>
                <Pencil className="size-3.5" />
                Edit
              </button>
            </div>
            {p.title ? <span className="text-xs text-muted-foreground">{p.title}</span> : null}
            {p.email ? <span className="truncate text-xs text-muted-foreground">{p.email}</span> : null}
            {p.phone ? (
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                <a href={`tel:${p.phone}`} className="hover:underline">{p.phone}</a>
                {whatsappLink(p.phone) ? (
                  <a href={whatsappLink(p.phone)!} target="_blank" rel="noreferrer" className={LINK_CHIP}>WhatsApp</a>
                ) : null}
              </span>
            ) : null}
            <Link to={paths.crm2Contact(workspaceSlug, p.id)} className={cn(LINK_CHIP, 'mt-1')}>
              Conversations and history
              <ArrowRight className="size-3" />
            </Link>
          </li>
          ),
        )}
        {!d.contacts.length ? <li className="px-4 py-3 text-sm text-muted-foreground">Nobody yet.</li> : null}
      </ul>
      <div className="grid gap-2 border-t border-border-soft p-3">
        {adding ? (
          <>
            <Input aria-label="Name" placeholder="Name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} className="h-8" />
            <Input aria-label="Title" placeholder="Title" value={c.title} onChange={(e) => setC({ ...c, title: e.target.value })} className="h-8" />
            <Input aria-label="Email" placeholder="Email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} className="h-8" />
            <Input aria-label="Contact number" type="tel" placeholder={`Contact number, e.g. ${PHONE_PLACEHOLDER}`} value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} className="h-8" />
            <div className="flex gap-2">
              <Button size="sm" disabled={!c.name.trim() || add.isPending} onClick={() => add.mutate({ name: c.name.trim(), title: c.title.trim() || null, email: c.email.trim() || null, phone: c.phone.trim() || null }, { onSuccess: () => { setC({ name: '', title: '', email: '', phone: '' }); setAdding(false) } })}>
                Add contact
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <Button size="sm" variant="outline" className="w-fit" onClick={() => setAdding(true)}>
            <Plus className="size-4" />
            Add contact
          </Button>
        )}
      </div>
    </Card>
  )
}

function Properties({ d }: { d: AccountDetail }) {
  const a = d.account
  const all = useCrm2Accounts('')
  const [f, setF] = useState({ name: a.name, domain: a.domain ?? '', industry: a.industry ?? '', size: a.size ?? '', ownerId: a.ownerId, renewalAt: dateInput(a.renewalAt), notes: a.notes ?? '' })
  const save = useCrm2Write(() => crm2Api.patchAccount(a.id, { name: f.name.trim(), domain: f.domain.trim() || null, industry: f.industry.trim() || null, size: f.size.trim() || null, ownerId: f.ownerId, renewalAt: fromDateInput(f.renewalAt), notes: f.notes.trim() || null }), () => 'Saved')
  const parent = useCrm2Write((parentId: string | null) => crm2Api.setParent(a.id, parentId), (r) => (r.parentId ? 'Now a sub-account' : 'Now a main account'))
  const options = (all.data ?? []).filter((x) => x.id !== a.id && !d.children.some((c) => c.id === x.id))
  return (
    <Card title="Account properties" defaultOpen={false}>
      <div className="grid gap-3 p-4">
        <Field label="Name">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label="Domain">
          <Input value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value })} />
        </Field>
        <Field label="Industry">
          <Input value={f.industry} onChange={(e) => setF({ ...f, industry: e.target.value })} />
        </Field>
        <Field label="Size">
          <Input value={f.size} onChange={(e) => setF({ ...f, size: e.target.value })} placeholder="200-500" />
        </Field>
        <Field label="Account owner">
          <PersonSelect label="Account owner" value={f.ownerId} onChange={(v) => setF({ ...f, ownerId: v })} />
        </Field>
        <Field label="Renews on" hint="A renewal task lands 90 days before.">
          <Input type="date" value={f.renewalAt} onChange={(e) => setF({ ...f, renewalAt: e.target.value })} />
        </Field>
        <Field label="Description and notes">
          <Textarea rows={4} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        </Field>
        <div>
          <Button size="sm" variant="outline" onClick={() => save.mutate(undefined)} disabled={save.isPending || !f.name.trim()}>
            Save properties
          </Button>
        </div>
        <Field label="Main account" hint="Leave empty if this is the main account itself.">
          <select className={fieldClass} value={d.parent?.id ?? ''} onChange={(e) => parent.mutate(e.target.value || null)} disabled={parent.isPending}>
            <option value="">None (main account)</option>
            {options.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </Card>
  )
}

/** Main account › sub-account › its deals › the people on each deal, and anyone else there. */
function Tree({ d, onOpen, onMoveTop }: { d: AccountDetail; onOpen: (id: string) => void; onMoveTop: (id: string) => void }) {
  const { workspaceSlug = '' } = useParams()
  const [shut, setShut] = useState<Set<string>>(() => new Set())
  const toggle = (id: string) => setShut((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n })
  const who = new Map<string, { name: string; title: string | null }>()
  for (const c of d.contacts) who.set(c.id, { name: c.name, title: c.title })
  for (const ch of d.children) for (const p of ch.people) who.set(p.id, p)
  const person = (id: string, role?: string) => {
    const p = who.get(id)
    if (!p) return null
    return (
      <li key={id} className="flex items-center gap-1.5 py-0.5 text-xs">
        <User className="size-3.5 text-text-muted" />
        <Link to={paths.crm2Contact(workspaceSlug, id)} className={LINK_CHIP}>{p.name}</Link>
        <span className="text-muted-foreground">{[p.title, role].filter(Boolean).join(' · ')}</span>
      </li>
    )
  }
  const dealNode = (x: Deal) => (
    <li key={x.id} className="py-1">
      <div className="flex items-center gap-1.5 text-sm">
        <Briefcase className="size-3.5 text-text-muted" />
        <button type="button" onClick={() => onOpen(x.id)} className={cn(LINK_CHIP, 'text-sm')}>{x.name}</button>
        <span className="text-xs text-muted-foreground">
          {x.status === 'open' ? `${x.groupLabel}${x.statusName ? ` · ${x.statusName}` : ''}` : x.status === 'won' ? 'Won' : 'Lost'}
        </span>
      </div>
      {x.contacts.length ? (
        <ul className="ml-5 border-l border-border-soft pl-3">
          {x.contacts.map((c) => person(c.contactId, c.role))}
        </ul>
      ) : null}
    </li>
  )
  const ownDealPeople = (deals: Deal[]) => new Set(deals.flatMap((x) => x.contacts.map((c) => c.contactId)))
  const mainOthers = d.contacts.filter((p) => !ownDealPeople(d.deals).has(p.id))
  return (
    <ul className="grid gap-1 rounded-lg border border-border-soft p-3">
      <li>
        <div className="flex items-center gap-1.5 text-sm font-semibold">
          <Building2 className="size-4 text-brand" />
          {d.account.name}
          <span className="text-xs font-normal text-muted-foreground">
            main account · {d.deals.length} deal{d.deals.length === 1 ? '' : 's'} · {d.contacts.length} {d.contacts.length === 1 ? 'person' : 'people'}
          </span>
        </div>
        <ul className="ml-6">
          {d.deals.map((x) => dealNode(x))}
          {mainOthers.length ? (
            <li className="py-1">
              <div className="text-xs font-medium text-muted-foreground">Not on a deal</div>
              <ul className="ml-2">{mainOthers.map((p) => person(p.id))}</ul>
            </li>
          ) : null}
        </ul>
      </li>
      {d.children.map((c) => {
        const deals = d.treeDeals.filter((x) => x.accountId === c.id)
        const onDeals = ownDealPeople(deals)
        const others = c.people.filter((p) => !onDeals.has(p.id))
        const open = !shut.has(c.id)
        return (
          <li key={c.id} style={{ marginLeft: `${c.depth * 1.25}rem` }} className="border-l border-border-soft pl-3">
            <div className="flex items-center gap-1.5 py-1 text-sm">
              <button type="button" onClick={() => toggle(c.id)} aria-expanded={open} aria-label={open ? `Collapse ${c.name}` : `Expand ${c.name}`} className="text-muted-foreground hover:text-foreground">
                <ChevronRight className={cn('size-4 transition-transform', open && 'rotate-90')} />
              </button>
              <Building2 className="size-4 text-muted-foreground" />
              <Link to={paths.crm2Account(workspaceSlug, c.id)} className={cn(LINK_CHIP, 'text-sm')}>{c.name}</Link>
              <span className="text-xs text-muted-foreground">
                {c.lifecycle ? `${c.lifecycle} · ` : ''}
                {deals.length} deal{deals.length === 1 ? '' : 's'} · {c.contacts} {c.contacts === 1 ? 'person' : 'people'}
              </span>
              {c.depth === 1 ? (
                <button type="button" onClick={() => onMoveTop(c.id)} className={cn(LINK_CHIP, 'ml-auto text-muted-foreground')}>Move to top level</button>
              ) : null}
            </div>
            {open ? (
              <ul className="ml-6">
                {deals.map((x) => dealNode(x))}
                {others.length ? (
                  <li className="py-1">
                    <div className="text-xs font-medium text-muted-foreground">Not on a deal</div>
                    <ul className="ml-2">{others.map((p) => person(p.id))}</ul>
                  </li>
                ) : null}
                {!deals.length && !others.length ? <li className="py-1 text-xs text-muted-foreground">No deals or people yet.</li> : null}
              </ul>
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}
